// ./src/pages/private/UsersPage/UsersPage.tsx
//
// Users management page for team/account administration.
//
// Architecture:
// - Wrapped in <UsersProvider> so child components (list, drawer, toolbar)
//   can share the same users state via useUsersContext()
// - UserEditDrawer reads get/update/isUpdating/lastError/clearError
//   from context automatically — only page-specific wrappers (onUpdated)
//   are passed as props for highlight side effects
// - UsersList reads data from context automatically
// - Uses ExpenseFiltersToolbar with showSearch enabled and all drawer
//   filters disabled. The toolbar manages the search keyword internally
//   and emits the combined filter object via onFiltersChange.
//
// Highlight behavior:
// - highlightedUserId persists until the next reload, add, or edit operation
//   (not on a timer), so the user can clearly see which record was affected
//
// Loading states:
// - Initial load (no data yet): UsersList shows skeleton loaders
// - Re-filtering (have data, fetching new): ContentLoadingOverlay dims the
//   existing list and shows a progress bar

import React, { useState, useCallback, useMemo, useRef } from "react";
import { Stack, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  DEFAULT_PAGE_SIZE,
  OPERATION_RIGHTS,
  ENTITIES,
  FEATURE_CODES,
} from "@/config";

import { buildFullName } from "@/utils/nameHelpers";
import { User } from "@/utils/api/UserService";

import { useApp } from "@/contexts/AppContext";
import { useAppEnvironment } from "@/contexts";
import { UsersProvider } from "@/contexts/UsersContext";
import { useUsersContext } from "@/contexts/UsersContext";

import { useCreateGate } from "@/hooks/useCreateGate";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import {
  useEntityPageShortcuts,
  useEntityPageShortcutHints,
  SHORTCUT_HINTS,
} from "@/hooks/useKeyboardShortcuts";

import { PageChrome, MobileHeaderAction } from "@/components/layouts";
import { withPlanGate } from "@/components/layouts/withPlanGate";

import { ConfirmModal } from "@/components/generic/ConfirmModal";
import { PullToRefresh } from "@/components/generic/PullToRefresh";
import { PlanFeatureNotice } from "@/components/generic/PlanFeatureNotice";
import { ContentLoadingOverlay } from "@/components/generic/ContentLoadingOverlay";

import { UsersList, UserEditDrawer, } from "@/components/features/Account/Users";
import { ShortcutHintBar } from "@/components/generic";

// ============================================
// Constants
// ============================================

/** API sort format for the provider */
const INITIAL_API_SORT = [{ name: "username", order: "ASC" as const }];

// ============================================
// Page Wrapper — provides UsersContext
// ============================================

/**
 * ⚠ GATED THROUGH `withPlanGate`, NOT BY WRAPPING THE PAGE IN `PremiumFeatureGate` HERE.
 *
 * The first attempt put the gate around `<UsersProvider>` — which sits OUTSIDE the page's own
 * `DashboardLayout`, so a locked page rendered the upgrade screen with **no sidebar and no
 * header**. A dead end the host could only leave with the browser's Back button.
 * `withPlanGate` renders the lock INSIDE the layout, which is the whole reason it exists.
 *
 * ⚠ It is wrapped HERE rather than bound in `siteMap.ts` because `max_users_qty` is NUMERIC: a
 * route has no count, and the rules answer "allowed" without one. `currentCount: 1` is the
 * question this page asks — the owner already holds seat 1, so 1 >= 1 locks a one-seat plan,
 * while a 5-seat plan passes and the page opens.
 */
const UsersPageInner: React.FC<PageProps> = (props: PageProps) => {
  return (
    /**
     * ⚠⚠ IT LOADS ON MOUNT. This used to pass `autoLoad={false}` and nothing on the page started the
     * first read — the list only filled because the app shell fires its header search's
     * `onSearch("")` 300ms after arrival, which several pages use as their loader. So the page
     * painted its empty state, waited out the debounce, then showed a skeleton, then the table
     * (owner, 2026-09-28). `useUsers` defaults `autoLoad` to FALSE, so this has to be said out loud.
     * ⓘ `usePurchaseOrders` and `useTags` default it to true, which is why those pages were right.
     */
    <UsersProvider
      autoLoad
      pageSize={DEFAULT_PAGE_SIZE}
      initialSort={INITIAL_API_SORT}
    >
      <UsersPageContent {...props} />
    </UsersProvider>
  );
};

export const UsersPage = withPlanGate(UsersPageInner, FEATURE_CODES.MAX_USERS_QTY, 'users', {
  currentCount: 1,
});

// ============================================
// Page Content — consumes UsersContext
// ============================================

const UsersPageContent: React.FC<PageProps> = () => {
  const { t } = useTranslation(["users", "common"]);
  const navigate = useNavigate();
  const { hasRightTo } = useApp();
  const { isMobile } = useAppEnvironment();

  // Navigation with filter preservation
  const {
    goToEdit,
    goToList,
    isEditMode,
    currentEntityId,
  } = useEntityNavigation({ entityType: "users", prefix: "team" });

  // Permissions
  const canCreate = hasRightTo(ENTITIES.USER_INVITATIONS, OPERATION_RIGHTS.CREATE);
  const canUpdate = hasRightTo(ENTITIES.USERS, OPERATION_RIGHTS.UPDATE);
  const canDelete = hasRightTo(ENTITIES.USERS, OPERATION_RIGHTS.REMOVE);

  // Shared users state from context
  const {
    users,
    loading,
    isLoaded,
    removingId,
    isUpdating,
    list,
    remove,
    clearError,
  } = useUsersContext();

  // ⚠ SEATS — and this page is the DECLARED EXCEPTION to `max_users_qty`'s gate kind.
  //
  // The feature is declared `gate: 'page'` (see the API's `appFeatures.ts`), which is right for
  // the INVITATIONS page: a one-seat plan can have no invitations, so replacing it with the
  // upgrade screen hides nothing. It is WRONG here — this page lists the account's users, and
  // there is always at least one (the owner). A page lock would hide their own record and the
  // drawer that edits it, both of which the server serves happily. So this page states the
  // exception with `gate="add"`; it does not compute one.
  //
  // ⚠ THE COUNT IS DELIBERATELY AN UNDER-COUNT. ms_auth's `UserInvitationCore.beforeCreate`
  // compares the ceiling against invitations + users; this page can only see users, and only
  // the loaded page of them. Under-counting fails OPEN — the invite is offered and the server
  // refuses it — which is the safe direction. The opposite (a client stricter than its server)
  // hides an action the host is entitled to, with no error anywhere to explain it.
  const seatGate = useCreateGate(FEATURE_CODES.MAX_USERS_QTY, { currentCount: users.length });

  // ============================================
  // Local UI State
  // ============================================

  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  /**
   * Highlighted user ID — persists until the next reload, add, or edit
   * operation clears it (not on a timer). This gives the user enough
   * time to find the affected record after a drawer closes.
   */
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(null);

  // Store latest filters from toolbar for use in refresh/pull-to-refresh
  /**
   * ⚠ THE KEYWORD IN STATE, NOT ONLY IN `currentFiltersRef`. A ref does not re-render, and the list
   * needs to know it is looking at a NARROWED result so an empty one reads "no match" rather than
   * "you have none yet" — telling somebody who just searched that their records are gone.
   */
  const [searchKeyword, setSearchKeyword] = useState("");

  const currentFiltersRef = useRef<Record<string, unknown> | null>(null);

  // ============================================
  // Derived State
  // ============================================

  /**
   * True when we already have data on screen and are fetching updated results.
   * Used to show ContentLoadingOverlay instead of skeleton loaders.
   */
  // ⚠ AND ITS COUNTERPART: a refetch of an EMPTY list must still say it is reloading. Keyed on
  // the row count, ⌘R on a page with no records showed NOTHING once the skeleton stopped
  // re-appearing — which is exactly how a working refresh gets reported as a dead key.
  const isRefiltering = loading && isLoaded;

  // ============================================
  // Data Loading
  // ============================================

  const listRef = useRef(list);
  listRef.current = list;

  /** Execute a list query with the given filter */
  const executeListQuery = useCallback(
    (filter: Record<string, unknown> = {}, shouldReset = true) => {
      // Clear highlight on reload
      setHighlightedUserId(null);

      // Build the API filter from the toolbar's combined filter
      const apiFilter: any = {};

      if (filter.searchKeyword) {
        apiFilter.keyword = filter.searchKeyword;
        apiFilter.searchIn = ["firstName", "lastName", "email", "username"];
      }

      return listRef.current(
        apiFilter,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        undefined, // use provider's initialSort
        shouldReset
      );
    },
    []
  );

  // ============================================
  // Handlers — Navigation
  // ============================================

  const handleEdit = useCallback(
    (user: User) => {
      if (!canUpdate || !user.id) return;
      clearError();
      goToEdit(user.id);
    },
    [goToEdit, clearError, canUpdate]
  );

  const handleDrawerClose = useCallback(() => {
    clearError();
    goToList();
  }, [goToList, clearError]);

  // ⚠ The gate lives INSIDE the handler, not on each button: the header action, the toolbar
  // Add and ⌘I all land here, and a guard on two of the three is a guard on none.
  const handleInviteUser = useCallback(() => {
    if (!seatGate.canCreate) {
      seatGate.goToUpgrade();

      return;
    }

    navigate("/team/invitations/add");
  }, [navigate, seatGate]);

  // ============================================
  // Handlers — Record lifecycle notifications from drawer
  // ============================================

  /** Called by the drawer after a user is successfully updated */
  const handleUserUpdated = useCallback(
    (user: User) => {
      // Clear previous highlight, set new one
      // Persists until next reload/operation
      setHighlightedUserId(user.id || null);
    },
    []
  );

  // ============================================
  // Handlers — Delete
  // ============================================

  const handleDeleteClick = useCallback(
    (user: User) => {
      if (!canDelete) return;
      setUserToDelete(user);
    },
    [canDelete]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete?.id) return;

    const result = await remove(userToDelete.id);

    if (result.code === OP_RESULT_CODES.OK) {
      setUserToDelete(null);
      // Clear highlight since list changed
      setHighlightedUserId(null);
      if (currentEntityId === userToDelete.id) {
        goToList();
      }
    }
  }, [userToDelete, remove, currentEntityId, goToList]);

  const handleCancelDelete = useCallback(() => {
    setUserToDelete(null);
  }, []);

  // ============================================
  // Handlers — Filters & List Controls
  // ============================================

  /**
   * ⚠⚠ THE SEARCH BOX LIVES IN THE APP SHELL'S HEADER, like every other list page. It used to be
   * inside a `ListToolbar` in the page body — a second place to look for the same control, on the
   * one page a new team member opens first, and the layout's ⌘F reached NOTHING because it focuses
   * the header box. Publishing it through `PageChrome` is what makes ⌘F, the phone header and the
   * search's position identical to Contacts, Venues, Tags and the rest.
   */
  const handleHeaderSearch = useCallback(
    (keyword: string) => {
      setSearchKeyword(keyword);
      const filters = keyword ? { searchKeyword: keyword } : {};

      currentFiltersRef.current = filters;
      executeListQuery(filters, true);
    },
    [executeListQuery]
  );

  const handleRefresh = useCallback(() => {
    executeListQuery(currentFiltersRef.current || {}, true);
  }, [executeListQuery]);

  const handlePullToRefresh = useCallback(async (): Promise<void> => {
    await executeListQuery(currentFiltersRef.current || {}, true);
  }, [executeListQuery]);

  // ============================================
  // Mobile Header Actions
  // ============================================

  const mobileHeaderActions = useMemo((): MobileHeaderAction[] => {
    const actions: MobileHeaderAction[] = [];

    // ⚠ Desktop/tablet only — phones refresh by pulling the list, so a second control in a small
    // header would be clutter. The same split every other list page uses.
    if (!isMobile) {
      actions.push({
        iconName: "Refresh",
        tooltip: `${t("common:buttons.refresh", { defaultValue: "Refresh" })} · ${SHORTCUT_HINTS.refresh}`,
        variant: "subtle",
        disabled: loading || isUpdating || !!removingId,
        onClick: handleRefresh,
      });
    }

    if (canCreate) {
      actions.push({
        iconName: seatGate.canCreate ? "UserPlus" : seatGate.addIconName,
        tooltip: seatGate.blockedTooltip ?? t("users:actions.invite", { defaultValue: "Invite User" }),
        variant: "filled",
        disabled: loading || isUpdating,
        onClick: handleInviteUser,
      });
    }

    return actions;
  }, [isMobile, canCreate, loading, isUpdating, removingId, handleInviteUser, handleRefresh, seatGate, t]);

  // ============================================
  // Keyboard Shortcuts
  // ============================================

  // List-level shortcuts: ⌘/Ctrl+I to invite, ⌘/Ctrl+R to refresh. Disabled
  // while the edit drawer is open — it registers its own save/close shortcuts.
  useEntityPageShortcuts({
    onAdd: handleInviteUser,
    onRefresh: handleRefresh,
    canAdd: canCreate,
    canRefresh: !loading,
    enabled: !isEditMode,
  });

  // ROADMAP T3 — the keys were wired; the bar that advertises them was not.
  // ⚠ ⌘F is NOT advertised here. The layout owns that key and focuses the HEADER search box, and
  // this page has none — its search lives in `ListToolbar`. Advertising a key that lands nowhere
  // reads as a broken page rather than as a page that differs.
  const shortcutHints = useEntityPageShortcutHints({
    hasAdd: canCreate,
    // ⌘S applies inside the edit drawer, which is where this page saves.
    hasSave: canUpdate,
    hasRefresh: true,
    // ⓘ Advertised since the search moved into the app shell's header — the layout owns ⌘F and it
    // now has a box to focus. It genuinely reached nothing while the search sat in the page body.
    hasSearch: true,
  });

  // ============================================
  // Delete Message
  // ============================================

  const deleteMessage = useMemo(() => {
    if (!userToDelete) return "";
    const name = buildFullName(userToDelete) || userToDelete.email || userToDelete.username || "";
    return t("users:delete.message", {
      name,
      defaultValue: "Are you sure you want to remove {{name}}? This action cannot be undone.",
    });
  }, [userToDelete, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <PageChrome
      mobileHeaderActions={mobileHeaderActions}
      headerSearch={{
        placeholder: t("users:search.placeholder", { defaultValue: "Search users..." }),
        onSearch: handleHeaderSearch,
      }}
    >
      <Stack gap="md" style={{ height: "100%", position: "relative" }}>
        <PlanFeatureNotice
          verdict={seatGate.verdict}
          onUpgrade={seatGate.goToUpgrade}
          what={t("users:anotherUser", { defaultValue: "another team member" })}
          gate="add"
        />

        {/* Users list with pull-to-refresh and refiltering overlay */}
        <Box style={{ flex: 1, minHeight: 0 }}>
          <ContentLoadingOverlay loading={isRefiltering}>
            <PullToRefresh onRefresh={handlePullToRefresh}>
              <UsersList
                hasQueryNarrowing={!!searchKeyword.trim()}
                onEdit={canUpdate ? handleEdit : undefined}
                onDelete={canDelete ? handleDeleteClick : undefined}
                onAdd={handleInviteUser}
                highlightedId={highlightedUserId}
              />
            </PullToRefresh>
          </ContentLoadingOverlay>
        </Box>

        <ShortcutHintBar hints={shortcutHints} />
      </Stack>

      {/* User edit drawer */}
      <UserEditDrawer
        opened={isEditMode}
        onClose={handleDrawerClose}
        userId={isEditMode ? currentEntityId : null}
        onUpdated={handleUserUpdated}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!userToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("users:delete.title", { defaultValue: "Delete User" })}
        message={deleteMessage}
        details={t("users:delete.details", {
          defaultValue: "The user will lose access to this account and all of its events.",
        })}
        confirmInProgress={removingId === userToDelete?.id}
        confirmLabel={t("common:buttons.delete", { defaultValue: "Delete" })}
        type="danger"
      />
    </PageChrome>
  );
};